import type { TargetareListaFisier } from "@/types/targetare";
import {
  ETICHETE_STARE_ACTUALIZARE,
  ETICHETE_TIP_LISTA,
  listaScadenta,
} from "@/utils/targetareConfig";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import type { ColoanaTabel } from "@/utils/targetareTabel";

export type RandLista = TargetareListaFisier & {
  cheie: string;
  scadenta: boolean;
  adaugate: number;
  eliminate: number;
  sursa_domeniu: string;
  autoritate_text: string;
  sector_text: string;
  actualizare_stare_text: string;
};

export function randuriListe(fisiere: TargetareListaFisier[]): RandLista[] {
  return fisiere.map((f) => {
    let domeniu = "";
    if (f.sursa_url) {
      try {
        domeniu = new URL(f.sursa_url).hostname.replace(/^www\./, "");
      } catch {
        domeniu = f.sursa_url;
      }
    }
    return {
      ...f,
      cheie: f.nume_fisier,
      scadenta: f.activ && listaScadenta(f.importat_la),
      adaugate: f.ultima_versiune?.adaugate ?? 0,
      eliminate: f.ultima_versiune?.eliminate ?? 0,
      sursa_domeniu: domeniu,
      autoritate_text: f.autoritate ?? "(fără)",
      sector_text: f.sector ?? "(fără serviciu)",
      actualizare_stare_text: f.actualizare_stare ?? "(niciodată)",
    };
  });
}

export const COLOANE_LISTE: ColoanaTabel<RandLista>[] = [
  {
    id: "nume_fisier",
    eticheta: "Listă (fișier)",
    tip: "text",
    latime: 320,
    implicit: true,
  },
  {
    id: "serviciu",
    eticheta: "Serviciu NIS2",
    tip: "text",
    latime: 240,
    implicit: true,
  },
  {
    id: "sector_text",
    eticheta: "Sector",
    tip: "lista",
    latime: 140,
    implicit: true,
  },
  {
    id: "tip",
    eticheta: "Tip",
    tip: "lista",
    latime: 100,
    implicit: true,
    optiuni: ["OFICIALA", "NEOFICIALA"],
    etichete: ETICHETE_TIP_LISTA,
  },
  { id: "activ", eticheta: "Activă", tip: "bool", latime: 80, implicit: true },
  {
    id: "autoritate_text",
    eticheta: "Autoritate",
    tip: "lista",
    latime: 130,
    implicit: true,
  },
  {
    id: "randuri",
    eticheta: "Rânduri",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "potrivite",
    eticheta: "Firme potrivite",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "in_scop",
    eticheta: "În scop",
    tip: "numar",
    latime: 90,
    implicit: true,
  },
  {
    id: "fara_dimensiune",
    eticheta: "Fără dimensiune",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "doar_lista",
    eticheta: "Tier B doar din listă",
    tip: "numar",
    latime: 110,
    implicit: false,
  },
  {
    id: "nepotrivite",
    eticheta: "Rânduri fără firmă",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "importat_la",
    eticheta: "Actualizată la",
    tip: "text",
    latime: 150,
    implicit: true,
  },
  {
    id: "scadenta",
    eticheta: "De actualizat",
    tip: "bool",
    latime: 100,
    implicit: true,
  },
  {
    id: "actualizare_automata",
    eticheta: "Actualizare automată",
    tip: "bool",
    latime: 110,
    implicit: true,
  },
  {
    id: "actualizare_stare_text",
    eticheta: "Ultima rulare automată",
    tip: "lista",
    latime: 130,
    implicit: true,
    optiuni: ["(niciodată)", "programata", "in_curs", "ok", "eroare"],
    etichete: ETICHETE_STARE_ACTUALIZARE,
  },
  {
    id: "actualizare_la",
    eticheta: "Rulată la",
    tip: "text",
    latime: 150,
    implicit: false,
  },
  {
    id: "actualizare_mesaj",
    eticheta: "Rezultatul rulării",
    tip: "text",
    latime: 320,
    implicit: false,
  },
  {
    id: "foaie",
    eticheta: "Foaia din sursă",
    tip: "text",
    latime: 140,
    implicit: false,
  },
  {
    id: "adaugate",
    eticheta: "Adăugate la ultima actualizare",
    tip: "numar",
    latime: 110,
    implicit: false,
  },
  {
    id: "eliminate",
    eticheta: "Eliminate la ultima actualizare",
    tip: "numar",
    latime: 110,
    implicit: false,
  },
  {
    id: "sursa_domeniu",
    eticheta: "Sursă",
    tip: "text",
    latime: 160,
    implicit: true,
  },
  {
    id: "sursa_url",
    eticheta: "Adresa sursei",
    tip: "text",
    latime: 260,
    implicit: false,
  },
  {
    id: "descriere",
    eticheta: "Observații",
    tip: "text",
    latime: 260,
    implicit: false,
  },
  {
    id: "service_id",
    eticheta: "Id serviciu",
    tip: "text",
    latime: 90,
    implicit: false,
  },
];

export const FILTRE_RAPIDE_LISTE: FiltruRapidDef[] = [
  {
    coloana: "tip",
    eticheta: "Tip",
    optiuni: ["OFICIALA", "NEOFICIALA"],
    etichete: ETICHETE_TIP_LISTA,
  },
  { coloana: "sector_text", eticheta: "Sector" },
  { coloana: "autoritate_text", eticheta: "Autoritate" },
  { coloana: "activ", eticheta: "Activă", optiuni: ["da", "nu"] },
  { coloana: "scadenta", eticheta: "De actualizat", optiuni: ["da", "nu"] },
  {
    coloana: "actualizare_automata",
    eticheta: "Actualizare automată",
    optiuni: ["da", "nu"],
  },
  {
    coloana: "actualizare_stare_text",
    eticheta: "Ultima rulare",
    optiuni: ["(niciodată)", "programata", "in_curs", "ok", "eroare"],
    etichete: ETICHETE_STARE_ACTUALIZARE,
  },
];

const IMPLICITE = COLOANE_LISTE.filter((c) => c.implicit).map((c) => c.id);

export const definitieRaportListe: DefinitieRaport<RandLista> = {
  cheie: "targetare.rapoarte.liste.v2",
  coloane: COLOANE_LISTE,
  rapoarteImplicite: [
    raportImplicit("Toate listele", IMPLICITE, {
      sortare: [{ id: "potrivite", desc: true }],
    }),
    raportImplicit("De actualizat (peste 30 de zile)", IMPLICITE, {
      filtre: [
        {
          id: "f-sc",
          coloana: "scadenta",
          operator: "egal",
          valoare: "da",
          activ: true,
        },
      ],
      sortare: [{ id: "importat_la", desc: false }],
    }),
    raportImplicit("Cu actualizare automată", IMPLICITE, {
      filtre: [
        {
          id: "f-auto",
          coloana: "actualizare_automata",
          operator: "egal",
          valoare: "da",
          activ: true,
        },
      ],
      sortare: [{ id: "actualizare_la", desc: true }],
    }),
    raportImplicit("Liste oficiale", IMPLICITE, {
      filtre: [
        {
          id: "f-of",
          coloana: "tip",
          operator: "egal",
          valoare: "OFICIALA",
          activ: true,
        },
      ],
    }),
    raportImplicit("Liste neoficiale", IMPLICITE, {
      filtre: [
        {
          id: "f-ne",
          coloana: "tip",
          operator: "egal",
          valoare: "NEOFICIALA",
          activ: true,
        },
      ],
    }),
    raportImplicit("Fără sursă cunoscută", IMPLICITE, {
      filtre: [
        {
          id: "f-fs",
          coloana: "sursa_domeniu",
          operator: "este_gol",
          activ: true,
        },
      ],
    }),
  ],
  cheieRand: (r) => r.cheie,
  filtreRapide: FILTRE_RAPIDE_LISTE,
  numeFisier: "liste-speciale",
  mesajGol: "Nu există liste încărcate.",
};
